/**
 * Parse episode and sub-episode information from filename
 * @param {string} filename - The filename to parse
 * @param {string} episodeName - The episode name (e.g., "Folge 2")
 * @returns {object} - {episode: number, subEpisode: number|null, type: string}
 */
export function parseEpisodeInfo(filename, episodeName) {
  // For MP3s: "Podcast 2-1.mp3" or "Podcast 2.mp3"
  const mp3Match = filename.match(/^Podcast\s+(\d+)(?:-(\d+))?\.mp3$/i);
  if (mp3Match) {
    return {
      episode: parseInt(mp3Match[1]),
      subEpisode: mp3Match[2] ? parseInt(mp3Match[2]) : null,
      type: 'mp3',
    };
  }

  // For PDFs: "2_1_GUYD_Workbook_Topic.pdf"
  const pdfMatch = filename.match(/^(\d+)_(\d+)_.*\.pdf$/);
  if (pdfMatch) {
    return {
      episode: parseInt(pdfMatch[1]),
      subEpisode: parseInt(pdfMatch[2]),
      type: 'pdf',
    };
  }

  // For WAV files: "Podcast 6-2.wav"
  const wavMatch = filename.match(/^Podcast\s+(\d+)(?:-(\d+))?\.wav$/i);
  if (wavMatch) {
    return {
      episode: parseInt(wavMatch[1]),
      subEpisode: wavMatch[2] ? parseInt(wavMatch[2]) : null,
      type: 'wav',
    };
  }

  // Fallback: extract from episode name "Folge 2"
  const episodeMatch = episodeName.match(/Folge\s+(\d+)/i);
  const fileType = filename.split('.').pop().toLowerCase();

  return {
    episode: episodeMatch ? parseInt(episodeMatch[1]) : null,
    subEpisode: null,
    type: fileType,
  };
}

/**
 * Transform flat episode data into hierarchical structure with sub-episodes
 * @param {object} apiResponse - The API response with episodes array
 * @returns {object} - Transformed data with hierarchical sub-episodes
 */
export function transformEpisodeData(apiResponse) {
  const episodes = apiResponse.episodes.map((episode) => {
    // Group files by sub-episode
    const subEpisodes = new Map();

    episode.files.forEach((file) => {
      // Skip the label file - it's metadata, not content
      if (file.filename === 'label') {
        return;
      }

      const info = parseEpisodeInfo(file.filename, episode.name);
      const subKey = info.subEpisode !== null ? info.subEpisode : 'main';

      if (!subEpisodes.has(subKey)) {
        subEpisodes.set(subKey, {
          subEpisodeNumber: info.subEpisode,
          mp3Files: [],
          pdfFiles: [],
          wavFiles: [],
        });
      }

      const subEp = subEpisodes.get(subKey);
      if (info.type === 'mp3') {
        subEp.mp3Files.push(file);
      } else if (info.type === 'wav') {
        subEp.wavFiles.push(file);
      } else {
        subEp.pdfFiles.push(file);
      }
    });

    // Convert map to sorted array
    const subEpisodesArray = Array.from(subEpisodes.entries())
      .map(([key, value]) => ({
        ...value,
        key: key,
      }))
      .sort((a, b) => {
        // Sort: numbered sub-episodes first, then 'main'
        if (a.subEpisodeNumber === null && b.subEpisodeNumber === null) return 0;
        if (a.subEpisodeNumber === null) return 1;
        if (b.subEpisodeNumber === null) return -1;
        return a.subEpisodeNumber - b.subEpisodeNumber;
      });

    return {
      name: episode.name,
      label: episode.label, // Optional label field
      featuredImage: episode.featuredImage,
      hasSubEpisodes:
        subEpisodesArray.length > 1 || (subEpisodesArray.length === 1 && subEpisodesArray[0].subEpisodeNumber !== null),
      subEpisodes: subEpisodesArray,
      // Keep raw files for backwards compatibility
      files: episode.files,
    };
  });

  return {
    ...apiResponse,
    episodes,
  };
}
